import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/** Fullscreen image viewer with zoom (double-tap / wheel) and swipe between photos. */
export default function ImageLightbox({ images = [], index = 0, onClose }) {
  const [i, setI] = useState(index);
  const [scale, setScale] = useState(1);

  const total = images.length;
  const go = (d) => {
    setScale(1);
    setI((p) => (p + d + total) % total);
  };
  const toggleZoom = () => setScale((s) => (s > 1 ? 1 : 2.5));

  return (
    <motion.div
      data-testid="lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-black/95"
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute right-5 top-6 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white"
      >
        <X className="h-6 w-6" />
      </button>

      {total > 1 && (
        <span className="absolute top-8 left-1/2 z-10 -translate-x-1/2 text-sm font-medium text-white/80">
          {i + 1} / {total}
        </span>
      )}

      <AnimatePresence mode="popLayout" initial={false}>
        <motion.img
          key={i}
          src={images[i]}
          alt=""
          drag={scale > 1 ? true : "x"}
          dragElastic={0.25}
          dragConstraints={scale > 1 ? undefined : { left: 0, right: 0, top: 0, bottom: 0 }}
          onDragEnd={(e, info) => {
            if (scale > 1) return;
            if (info.offset.x < -80) go(1);
            else if (info.offset.x > 80) go(-1);
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            toggleZoom();
          }}
          onWheel={(e) =>
            setScale((s) => Math.min(4, Math.max(1, s + (e.deltaY < 0 ? 0.25 : -0.25))))
          }
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, scale }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="max-h-[86vh] max-w-[92vw] cursor-zoom-in touch-none select-none object-contain"
          draggable={false}
        />
      </AnimatePresence>

      {total > 1 && scale === 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/50">
        Doble toque para acercar
      </span>
    </motion.div>
  );
}
